import React, { useState, useEffect } from 'react';
import Table from '../../molecules/tablas/view';
import CustomBox from '../../atoms/container/container';
import { getComprobantes, getDocumentTypes } from '../../../usecases/accounting-usecase';
import { mapAccountingVouchers } from '../../../infrastructure/mappers/accounting-mapper.js';
import { EmptyArray } from '../../../app/domain/models/empty/empty';
import { listener, dispatchCustomEvent } from '../../../utils/dispatch';
import ComprobanteView from '../../organisms/comprobanteView/view';
import ButtonAction from '../../atoms/buttonAction/view';
import { BiCopy, BiEdit, BiTrash } from 'react-icons/bi';
import { Calendar, FileText } from 'lucide-react';
import FacetedFilter from '../../molecules/FacetedFilter/view';
import './style.css';

// Componente para Badges de Tipo de Documento
const DocTypeBadge = ({ type }) => {
    const colors = {
        'FV': { bg: 'rgba(16, 185, 129, 0.1)', text: '#10b981', label: 'FACTURA' },
        'CG': { bg: 'rgba(59, 130, 246, 0.1)', text: '#3b82f6', label: 'GASTO' },
        'ND': { bg: 'rgba(245, 158, 11, 0.1)', text: '#f59e0b', label: 'NOTA DEB' },
        'NC': { bg: 'rgba(239, 68, 68, 0.1)', text: '#ef4444', label: 'NOTA CR' },
        'default': { bg: 'rgba(107, 114, 128, 0.1)', text: '#6b7280', label: type }
    };
    const style = colors[type] || colors.default;
    return (
        <span className="doc-type-badge-premium" style={{ backgroundColor: style.bg, color: style.text }}>
            {style.label}
        </span>
    );
};

const AccountingComprobantesTable = ({ profile }) => {
    const [activeFilters, setActiveFilters] = useState([]);
    const [appliedFilters, setAppliedFilters] = useState({
        dateStart: null,
        dateEnd: null,
        documentType: '',
    });

    const [comprobantes, setComprobantes] = useState({
        total: 0,
        page: 1,
        page_size: 20,
        data: [],
    });

    const [documentTypes, setDocumentTypes] = useState([]);
    const [loading, setLoading] = useState(false);

    const filtersConfig = [
        { key: 'dateStart', label: 'Fecha Desde', icon: <Calendar size={14} />, type: 'date' },
        { key: 'dateEnd', label: 'Fecha Hasta', icon: <Calendar size={14} />, type: 'date' },
        { 
            key: 'documentType', 
            label: 'Tipo de Documento', 
            icon: <FileText size={14} />, 
            type: 'select', 
            options: documentTypes.map(t => ({ value: t.key || t.id, label: t.text || t.name }))
        }
    ];

    useEffect(() => {
        const filters = {
            dateStart: null,
            dateEnd: null,
            documentType: '',
        };
        activeFilters.forEach(f => {
            if (f.key === 'dateStart') {
                filters.dateStart = new Date(f.value + 'T00:00:00');
            }
            if (f.key === 'dateEnd') {
                filters.dateEnd = new Date(f.value + 'T23:59:59');
            }
            if (f.key === 'documentType') {
                filters.documentType = f.value;
            }
        });
        setAppliedFilters(filters);
    }, [activeFilters]);

    useEffect(() => {
        update();
        getDocumentTypes().then(res => {
            setDocumentTypes(res);
        });
    }, []);

    const update = (page = comprobantes.page, limit = comprobantes.page_size) => {
        setLoading(true);
        setComprobantes(prev => ({ ...prev, data: [] }));

        // Preparar filtros para la API
        const apiFilters = {};
        if (appliedFilters.dateStart) {
            apiFilters.dateStart = appliedFilters.dateStart.toISOString().split('T')[0];
        }
        if (appliedFilters.dateEnd) {
            apiFilters.dateEnd = appliedFilters.dateEnd.toISOString().split('T')[0];
        }
        if (appliedFilters.documentType) {
            apiFilters.documentType = appliedFilters.documentType;
        }

        getComprobantes(page, limit, apiFilters).then(res => {
            const data = res.data;
            if (data.data.length === 0) {
                setComprobantes(prev => ({ ...prev, data: EmptyArray }));
            } else {
                const mappedData = mapAccountingVouchers(data.data).map(v => ({
                    ...v,
                    date: v.date,
                    descripcion: v.descripcion || v.concept || 'N/A',
                    documento: v.document || 'N/A',
                    numero: v.number || v.document_number || 'N/A',
                    tipo: v.document_type || 'N/A'
                }));
                setComprobantes({
                    ...data,
                    data: mappedData
                });
            }
        }).catch(err => {
            console.error("Error fetching comprobantes:", err);
        }).finally(() => {
            setLoading(false);
        });
    };

    useEffect(() => {
        update();
    }, [appliedFilters]); // Solo actualizar cuando se apliquen los filtros

    useEffect(() => {
        const removeListener = listener("reload-comprobantes", () => update(comprobantes.page, comprobantes.page_size));
        return () => {
            if (typeof removeListener === 'function') removeListener();
        };
    }, [comprobantes.page, comprobantes.page_size, appliedFilters]);

    return (
        <CustomBox style={{ maxHeight: '90%' }}>
            <div className="comprobantes-filters-row" style={{ marginBottom: '16px', display: 'flex', justifyContent: 'flex-end' }}>
                <FacetedFilter
                    filters={filtersConfig}
                    activeFilters={activeFilters}
                    onChange={setActiveFilters}
                    onClear={() => setActiveFilters([])}
                />
            </div>
            <Table
                paginatedData={comprobantes}
                getFunction={update}
                isLoading={loading}
                id="accounting-comprobantes-table"
                headersList={["Fecha", "Descripción", "Doc. Asociado", "# Documento", "Tipo"]}
                excludeList={["id", "lineas", "lines", "created_at", "user_id", "is_manual", "updated_at", "document_type", "document", "document_number", "number", "concept", "aditional_info"]}
                formats={{
                    date: { type: "timestamp" },
                    tipo: { type: "custom", children: (item) => <DocTypeBadge type={item.tipo} /> },
                    descripcion: { type: "tooltip" }
                }}
                allowExport={true}
                exportFileName="comprobantes-contables"
                allowPageSizeControl={true}
            >
                <ComprobanteView profile={profile} />
                <ButtonAction
                    title={<BiEdit />}
                    enableLoadingAnimation={true}
                    disabled={(item) => item?.is_manual != 1}
                    onClick={(item) => {
                        dispatchCustomEvent("edit-comprobante-data", item);
                        dispatchCustomEvent('loading-btn', null);
                    }}
                />
                <ButtonAction
                    title={<BiCopy />}
                    enableLoadingAnimation={true}
                    onClick={(item) => {
                        dispatchCustomEvent("clone-comprobante-data", item);
                        dispatchCustomEvent('loading-btn', null);
                    }}
                />
                <ButtonAction
                    title={<BiTrash />}
                    enableLoadingAnimation={true}
                    onClick={(item) => {
                        dispatchCustomEvent("anular-comprobante-data", item);
                        dispatchCustomEvent('loading-btn', null);
                    }}
                />
            </Table>
        </CustomBox>
    );
};

export default AccountingComprobantesTable;
